'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { IconButton } from '../../../../../components/icon-button';
import { Text } from '../../../../../components/styled/text';
import { Undo02Icon, FloppyDiskIcon } from '@terra/ui-icons';
import { MarkdownPreview } from '../../../../markdown-preview';
import { MarkdownEditor } from '../../MarkdownEditor';
import { useTranslations } from 'next-intl';
import {
  formatHttpRequest,
  formatArgsToHttpText,
  parseHttpTextToArgs,
} from '../utils/http-formatters';

type HttpRequestDisplayProps = {
  args: Record<string, unknown>;
  compact?: boolean;
  isEditing?: boolean;
  onSave?: (editedArgs: Record<string, unknown>) => void;
  onCancel?: () => void;
};

export function HttpRequestDisplay({
  args,
  compact = false,
  isEditing = false,
  onSave,
  onCancel,
}: HttpRequestDisplayProps) {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const originalUrl = (args.url as string) || '';
  const originalCredentialId = args.credentialId as string | undefined;
  const originalHttpText = useMemo(() => formatArgsToHttpText(args), [args]);
  const [editText, setEditText] = useState(originalHttpText);

  useEffect(() => {
    setEditText(originalHttpText);
  }, [originalHttpText]);
  const handleSave = useCallback(() => {
    const editedArgs = parseHttpTextToArgs(
      editText,
      originalUrl,
      originalCredentialId,
    );

    if (!editedArgs) {
      return;
    }

    onSave?.(editedArgs);
  }, [editText, originalUrl, originalCredentialId, onSave]);
  const handleReset = useCallback(() => {
    setEditText(originalHttpText);
    onCancel?.();
  }, [originalHttpText, onCancel]);
  const httpRequest = useMemo(() => formatHttpRequest(args), [args]);

  if (isEditing) {
    return (
      <Box width="100%" borderRadius="md" overflow="hidden" bg="bg.subtle">
        <Box
          px={3}
          py={2}
          bg="bg.muted"
          borderBottomWidth="1px"
          borderColor="border.default"
        >
          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('editRequest')}
            </Text>
            <HStack gap={1}>
              <IconButton
                size="xs"
                variant="ghost"
                onClick={handleReset}
                aria-label="Reset"
              >
                <Undo02Icon size={12} />
              </IconButton>
              <IconButton
                size="xs"
                variant="solid"
                onClick={handleSave}
                aria-label="Save"
              >
                <FloppyDiskIcon size={12} />
              </IconButton>
            </HStack>
          </HStack>
        </Box>

        <Box minH="150px">
          <MarkdownEditor
            value={editText}
            onChange={setEditText}
            height="200px"
          />
        </Box>
      </Box>
    );
  }

  if (!httpRequest) {
    return null;
  }

  const { requestLine, headers, body } = httpRequest;
  let httpContent = requestLine;

  if (headers.length > 0) {
    httpContent += '\n' + headers.join('\n');
  }

  if (body && !compact) {
    httpContent += '\n\n' + body;
  }

  const httpMarkdown = `\`\`\`http\n${httpContent}\n\`\`\``;

  return (
    <Box width="100%" borderRadius="md" overflow="hidden" bg="bg.subtle">
      <Box
        px={3}
        py={2}
        bg="bg.muted"
        borderBottomWidth="1px"
        borderColor="border.default"
      >
        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
          {t('request')}
        </Text>
      </Box>

      <Box>
        <MarkdownPreview source={httpMarkdown} />
      </Box>
    </Box>
  );
}
